import PropTypes from 'prop-types';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import { PlainButton, orange, tokens, useIsMobile } from 'components/v3';
import Glyph from '../../Forms/Submissions/Glyph';
import { sx } from '../../Forms/Submissions/sx';

// Tax-year switcher (design W9Parts.YearChips, w9-plan §6.3 item 2): "All
// years", then every year on file newest first with its count. The
// current year carries a green dot, archived years the archive glyph. A
// scrolling row on phones.

const Chip = styled(PlainButton)`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 10px 0 13px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$on ? orange(1) : p.$border)};
  background: ${(p) => (p.$on ? orange(0.1) : p.$card)};
  color: ${(p) => (p.$on ? p.$text : p.$sub)};
  font-size: 15px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  &:hover {
    border-color: ${(p) => (p.$on ? orange(1) : p.$hoverBorder)};
  }
`;

const Count = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: ${(p) => (p.$on ? orange(1) : p.$fill)};
  color: ${(p) => (p.$on ? 'white' : p.$sub)};
  font-size: 11px;
  font-weight: 700;
`;

// The year chips to show: every year the server returned, plus the one
// in the URL when it isn't among them (zero rows, still a valid year).
export const chipYears = (years, selected) => {
  const list = [...(years || [])].sort((a, b) => b.year - a.year);
  if (typeof selected === 'number' && !list.some((y) => y.year === selected)) {
    const current = list.find((y) => y.status === 'current');
    list.push({
      year: selected,
      count: 0,
      status: current && selected < current.year ? 'archived' : 'current',
    });
    list.sort((a, b) => b.year - a.year);
  }
  return list;
};

const YearChips = ({ years, active, onPick }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const s = sx(theme);
  const isMobile = useIsMobile();
  const chipProps = (on) => ({
    $on: on,
    $border: t.border,
    $hoverBorder: s.line2,
    $card: t.card,
    $text: t.text,
    $sub: t.sub,
  });
  const countProps = (on) => ({ $on: on, $fill: s.fill, $sub: t.sub });
  const list = chipYears(years, active);
  return (
    <div
      role="tablist"
      aria-label="Tax year"
      style={{
        display: 'flex',
        gap: 8,
        minWidth: 0,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        margin: isMobile ? '0 -12px' : 0,
        padding: isMobile ? '0 12px' : 0,
      }}
    >
      <Chip
        role="tab"
        aria-selected={active === 'all'}
        onClick={() => onPick('all')}
        {...chipProps(active === 'all')}
      >
        <Glyph name="layers" size={14} />
        All years
      </Chip>
      {list.map((y) => {
        const on = active === y.year;
        return (
          <Chip
            key={y.year}
            role="tab"
            aria-selected={on}
            aria-label={`${y.year}, ${
              y.status === 'current' ? 'current' : 'archived'
            }, ${y.count} on file`}
            onClick={() => onPick(y.year)}
            {...chipProps(on)}
          >
            {y.status === 'current' ? (
              <span
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: s.ok,
                  boxShadow: `0 0 0 3px ${s.okBg}`,
                }}
              />
            ) : (
              <Glyph name="archive" size={14} />
            )}
            {y.year}
            <Count {...countProps(on)}>{y.count.toLocaleString()}</Count>
          </Chip>
        );
      })}
    </div>
  );
};

YearChips.propTypes = {
  years: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.number.isRequired,
      count: PropTypes.number.isRequired,
      status: PropTypes.string.isRequired,
    })
  ),
  active: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['all'])])
    .isRequired,
  onPick: PropTypes.func.isRequired,
};

YearChips.defaultProps = { years: [] };

export default YearChips;
